/* global gigya */
import {cloneElement, useContext, useEffect} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import {leadPhoneCallTarget, leadPhoneCallType} from '@adv-mt/mt-lead-phone-call'
import IconMail from '@adv-mt/mt-svg-icons/lib/MailOutline'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {useChecksAndValidations} from '../../hooks/useChecksAndValidations.js'
import useContactSellerFormContext from '../../hooks/useContactSellerFormContext.js'
import {FIELDS} from '../../useContactSellerState/reducer.js'
import {saveLeadEnrichmentToLocalStorage} from '../../utils.js'

const BUTTON_SIZE = atomButtonSizes.LARGE

const ContactButtons = ({leadEnrichmentValue, handleBlockSellerToast, showLeadEnrichment}) => {
  const {track} = useAnalyticsTrack()
  const {domain, i18n, logger} = useContext(Context)
  const {state, actions, currentProps} = useContactSellerFormContext()
  const {isLogged} = useContext(AuthenticationContext)
  const {triggerLoginModal, isLoginModalOpen} = useContext(LoginModalContext)

  const config = domain.get('config')
  const errors = config.get('ERRORS')
  const {PRIVATE_CONTRACT_ID} = config.get('dealer')
  const {LOGIN_SOURCE_ID_LEAD} = config.get('login')

  const {LAST_CONTACT_DETAIL_KEY} = config.get('contact')
  const {CONTACT_DEFAULT_MESSAGE} = config.get('COMMON')
  const {
    HIT_ORIGINS,

    TRACKING_EVENT_NAMES: {SIGNED_UP, SIGNED_UP_ERROR}
  } = config.get('tracking')

  const {
    adId,
    buttonCallSeller,
    // abtest4_web_lead_experience_whatsapp_button
    buttonWhatsapp,
    contractId,
    isModal,
    isSubscriptionLead,
    isZipCodeEnabled,
    pageName = '',
    hideButtonsSeparator,
    enableAuthBeforeLead,
    subscriptionPriceOptionSelected,
    targetBuyer
  } = currentProps

  const {areFormFieldsValid, hasSuccessfulCaptcha, isValidName, isValidPhone, isValidEmail} = useChecksAndValidations({
    isUseZipCode: isZipCodeEnabled
  })

  const {
    updateErrorsFields,
    setUserSubmitted,
    setUserContact,
    setFormContactErrors,
    setIsFormSubmitted,
    setAuthBeforeLeadResult
  } = actions

  // Mirrors how LeadPhoneCall decides who is private, so both lead surfaces agree.
  const isPrivateSeller = Number(contractId) === PRIVATE_CONTRACT_ID

  // A private seller's lead is blocked until the user authenticates. Resume lead on success. PRO does the opposite (auto-register during submit), so the two paths are exclusive.
  const requiresAuthBeforeLead = isPrivateSeller && !isLogged

  useEffect(() => {
    if (state.isFormSubmitted) {
      if (requiresAuthBeforeLead) {
        if (!isLoginModalOpen) {
          triggerLoginModal({
            modalProps: {
              source: LOGIN_SOURCE_ID_LEAD,
              trackingOrigin: HIT_ORIGINS.LEAD_EMAIL,
              onLogin: handleSubmit,
              onClose: () => setIsFormSubmitted(false)
            }
          })
        }

        handleBlockSellerToast(false)
        return
      }

      handleSubmit()
      handleBlockSellerToast(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isFormSubmitted])
  const hasAcceptedTermsAndConditions = state[FIELDS.TERMS_AND_CONDITIONS.NAME].value

  const updateErrorFields = () => {
    updateErrorsFields({
      [FIELDS.NAME.NAME]: !isValidName,
      [FIELDS.EMAIL.NAME]: !isValidEmail,
      [FIELDS.PHONE.NAME]: !isValidPhone,
      [FIELDS.TERMS_AND_CONDITIONS.NAME]: !hasAcceptedTermsAndConditions
    })
  }

  const getContactSeller = ({
    adId,
    captchaToken,
    contractId,
    context,
    name,
    email,
    phone,
    message,
    hasAcceptedTermsAndConditions,
    zipCode
  }) =>
    domain.get('contact_seller_use_case').execute({
      adId,
      captchaToken,
      contractId,
      context,
      name,
      email,
      phone,
      message,
      hasAcceptedTermsAndConditions,
      zipCode,
      buyerIntentions: leadEnrichmentValue,
      isDefaultMessage: message === CONTACT_DEFAULT_MESSAGE,
      subscriptionCommitment:
        isSubscriptionLead && subscriptionPriceOptionSelected
          ? {
              totalMonths: subscriptionPriceOptionSelected.totalMonths,
              monthlyPrice: subscriptionPriceOptionSelected.monthlyPrice,
              kmIncluded: subscriptionPriceOptionSelected.kmIncluded,
              kmIncludedPeriod: subscriptionPriceOptionSelected.kmIncludedPeriod,
              targetBuyer
            }
          : undefined
    })

  const generateRandomPassword = () => {
    const base = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
    return base.slice(0, 10) + 'A1!'
  }

  // TODO [DEBT]: when rollout move to domain/refactor
  const mapConsentsToGigyaPreferences = consents => {
    const preferences = {}
    Object.entries(consents).forEach(([key, isConsentGranted]) => {
      const parts = key.split('.')
      if (parts.length === 2) {
        const [category, consentId] = parts
        if (!preferences[category]) preferences[category] = {}
        preferences[category][consentId] = {isConsentGranted}
      } else {
        preferences[key] = {isConsentGranted}
      }
    })
    return preferences
  }

  const tryRegisterAndAuthenticateWithGigya = async ({email}) => {
    const autoGeneratedPassword = generateRandomPassword()
    try {
      const [, defaultConsents] = await domain.get('get_default_consents_user_use_case').execute()
      const preferences = defaultConsents ? mapConsentsToGigyaPreferences(defaultConsents) : undefined

      const gigyaRegistrationToken = await new Promise((resolve, reject) => {
        gigya.accounts.initRegistration({
          callback: response => {
            if (response.errorCode === 0) resolve(response.regToken)
            else reject(new Error(response.errorMessage))
          }
        })
      })

      const gigyaRegistrationResponse = await new Promise((resolve, reject) => {
        let settled = false
        gigya.accounts.register({
          email,
          password: autoGeneratedPassword,
          regToken: gigyaRegistrationToken,
          finalizeRegistration: true,
          preferences,
          profile: {firstName: state[FIELDS.NAME.NAME].value},
          callback: response => {
            if (settled) return
            if (response.errorCode === 0) {
              settled = true
              resolve(response)
            } else {
              settled = true
              track({event: SIGNED_UP_ERROR, data: {errorCode: response.errorCode}})
              reject(new Error(response.errorMessage))
            }
          }
        })
      })

      await domain
        .get('login_authentication_use_case')
        .execute({gigyaResponse: gigyaRegistrationResponse.userInfo ?? gigyaRegistrationResponse})

      track({event: SIGNED_UP, data: {hit_origin: HIT_ORIGINS.LEAD_EMAIL}})

      return {autoGeneratedPassword}
    } catch (err) {
      track({event: SIGNED_UP_ERROR, data: {hit_information: 'generic_error'}})
      console.error(err?.message, err)
      return {autoGeneratedPassword: null}
    }
  }

  const tryEmailCheckAndRegisterIfNeeded = async ({email}) => {
    try {
      const [, result] = await domain.get('check_user_email_exists_user_use_case').execute({email})
      if (result && !result.exists) {
        const {autoGeneratedPassword} = await tryRegisterAndAuthenticateWithGigya({email})
        return {userHasBeenRegistered: true, autoGeneratedPassword}
      }
      return {userHasBeenRegistered: false, autoGeneratedPassword: null}
    } catch (err) {
      console.error(err?.message, err)
      return {userHasBeenRegistered: false, autoGeneratedPassword: null}
    }
  }

  const handleSubmit = async () => {
    setUserSubmitted()
    if (enableAuthBeforeLead) {
      const {userHasBeenRegistered, autoGeneratedPassword} = await tryEmailCheckAndRegisterIfNeeded({
        email: state[FIELDS.EMAIL.NAME].value
      })
      setAuthBeforeLeadResult({userHasBeenRegistered, autoGeneratedPassword})
    }

    const [contactSellerError, contactSellerResponse] = await getContactSeller({
      adId,
      captchaToken: state.captchaToken,
      contractId,
      context: pageName,
      name: state[FIELDS.NAME.NAME].value,
      email: state[FIELDS.EMAIL.NAME].value,
      phone: state[FIELDS.PHONE.NAME].value,
      message: state[FIELDS.MESSAGE.NAME].value || i18n.t('FORM.CONTACT_MESSAGE_PLACEHOLDER'),
      hasAcceptedTermsAndConditions: state[FIELDS.TERMS_AND_CONDITIONS.NAME].value,
      zipCode: state[FIELDS.ZIPCODE.NAME].value
    })

    if (contactSellerError) {
      logger?.error({
        name: `[ContactSeller]: "adId: ${adId} | contractId: ${contractId}" error in page: ${pageName}`,
        message: `[ContactSeller]: ${contactSellerError?._message}, status: ${
          contactSellerError?._status
        }, error: ${JSON.stringify(contactSellerError?._error)}`
      })
    }

    if (contactSellerResponse) {
      setUserContact({messageContactId: contactSellerResponse.messageContactId})

      if (showLeadEnrichment) {
        saveLeadEnrichmentToLocalStorage({
          localStorage,
          leadEnrichmentValue,
          key: LAST_CONTACT_DETAIL_KEY
        })

        leadEnrichmentValue?.length &&
          track({
            event: 'Lead Enriched',
            data: {
              hit_information: leadEnrichmentValue.join(':')
            }
          })
      }
    }

    if (contactSellerError) {
      const contactSellerFormErrors = contactSellerError.getErrors?.()

      if (!contactSellerFormErrors) return setFormContactErrors()

      const hasNameError = contactSellerFormErrors.find(err => err instanceof errors.EMPTY_NAME_SELLER_ERROR)
      const hasEmailError = contactSellerFormErrors.find(
        err => err instanceof errors.EMPTY_EMAIL_SELLER_ERROR || err instanceof errors.INVALID_EMAIL_SELLER_ERROR
      )
      const hasPhoneError = contactSellerFormErrors.find(err => err instanceof errors.INVALID_PHONE_SELLER_ERROR)
      const hasGenericError = contactSellerFormErrors.find(err => err instanceof errors.GENERIC_SELLER_SELLER_ERROR)

      const hasControlledError = hasNameError || hasEmailError || hasPhoneError || hasGenericError

      if (hasControlledError)
        updateErrorsFields({
          [FIELDS.NAME.NAME]: hasNameError,
          [FIELDS.EMAIL.NAME]: hasEmailError,
          [FIELDS.PHONE.NAME]: hasPhoneError,
          hasGenericError
        })
    }
  }

  const handleSubmitContactButton = () => {
    const areAllFieldsValid = areFormFieldsValid()

    updateErrorFields()

    if (state.isSellerBlocked) {
      handleBlockSellerToast(true)
      return
    }

    if (!areAllFieldsValid || hasSuccessfulCaptcha) {
      return
    }

    setIsFormSubmitted(true)
  }

  const showButtonCallSeller = !!buttonCallSeller

  const contactButtonsClass = cx(
    'mt-FormContactSeller-contactButtons',
    showButtonCallSeller && !buttonWhatsapp && `mt-FormContactSeller-contactButtons--twoRows`
  )

  return (
    <div className={contactButtonsClass}>
      <AtomButton
        color={atomButtonColors.ACCENT}
        design={atomButtonDesigns.SOLID}
        disabled={state.isSellerBlocked && isModal}
        fullWidth
        leftIcon={<IconMail />}
        isLoading={state.isWaitingForResponse}
        onClick={e => {
          e.preventDefault()
          handleSubmitContactButton()
        }}
        shape={atomButtonShapes.CIRCULAR}
        size={BUTTON_SIZE}
      >
        {i18n.t('COMMON_CONTACT')}
      </AtomButton>
      {!hideButtonsSeparator && showButtonCallSeller && (
        <div className="mt-FormContactSeller-contactButtons-divider">
          <span className="mt-FormContactSeller-contactButtons-divider-circle">o</span>
        </div>
      )}

      {showButtonCallSeller && !buttonWhatsapp && buttonCallSeller && (
        <div className="mt-FormContactSeller-callSeller">
          {cloneElement(buttonCallSeller, {
            size: 'large',
            type: leadPhoneCallType.button,
            ...(leadEnrichmentValue?.includes('FINANCING') && {target: leadPhoneCallTarget.financingDealer})
          })}
        </div>
      )}

      {/* abtest4_web_lead_experience_whatsapp_button */}
      {buttonWhatsapp && (
        <>
          <div className="mt-FormContactSeller-contactButtons-divider">
            <span className="mt-FormContactSeller-contactButtons-divider-circle">o</span>
          </div>
          <div className="mt-FormContactSeller-buttonWhatsapp">
            {showButtonCallSeller && buttonCallSeller}
            {buttonWhatsapp}
          </div>
        </>
      )}
    </div>
  )
}

ContactButtons.displayName = 'FormContactSeller'

/*
 * Props adId and contractId define the contact target (ad or dealer)
 * Props search, vehicle, province and redirectTo props are only used for contact from an AD
 */

ContactButtons.propTypes = {
  leadEnrichmentValue: PropTypes.arrayOf(PropTypes.string),
  handleBlockSellerToast: PropTypes.func,
  showLeadEnrichment: PropTypes.bool
}

export default ContactButtons
